import { AlertTriangle, CheckCircle2, HeartPulse, Info, Loader2 } from "lucide-react";
import { Badge } from "@groot/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@groot/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { EmptyState } from "@groot/ui/empty-state";
import { useDataHealthReport } from "@/hooks/api";
import { formatDate } from "@/lib/utils";
import type { DataHealthBalanceCheck, DataHealthSeverity } from "@/types";

/**
 * Data Health panel (issue #213 step 4, plan §7) — Settings → Data Health.
 *
 * Read-only by design: it surfaces violations with severity + suggested fix
 * per rule, grouped by rule, plus the §7.1 per-account balance table with
 * exact deltas and staleness warnings. Fixes happen in the surfaces each
 * suggested fix names (Transactions, Settings → Transfers, Jobs); this panel
 * never mutates anything.
 */

function severityBadge(severity: DataHealthSeverity) {
  const label = severity === "ok" ? "OK" : severity[0].toUpperCase() + severity.slice(1);
  const variant =
    severity === "error" ? "destructive" : severity === "warning" ? "default" : "secondary";
  return <Badge variant={variant}>{label}</Badge>;
}

function BalanceStatusIcon({ status }: { status: DataHealthBalanceCheck["status"] }) {
  if (status === "match") return <CheckCircle2 className="size-4 text-muted-foreground" />;
  if (status.startsWith("mismatch")) return <AlertTriangle className="size-4 text-destructive" />;
  return <Info className="size-4 text-muted-foreground" />;
}

function BalanceRow({ check }: { check: DataHealthBalanceCheck }) {
  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="flex items-center gap-2">
          <BalanceStatusIcon status={check.status} />
          <div>
            <p className="text-sm font-medium text-foreground">
              {check.accountName}
              {check.isArchived && <span className="text-muted-foreground"> · archived</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              {check.accountType}
              {check.asOf ? ` · statement as of ${formatDate(check.asOf)}` : ""}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="align-top text-xs text-muted-foreground">
        <p>Recorded: {check.recordedBalance != null ? check.recordedBalance : "—"}</p>
        <p>Expected: {check.expectedBalance != null ? check.expectedBalance : "—"}</p>
        <p>Flows since statement: {check.flowsSinceAsOf ?? "—"}</p>
        <p>{check.transactionCount} transaction(s) scanned</p>
      </TableCell>
      <TableCell className="align-top">
        {severityBadge(check.severity)}
        <p className="mt-1 max-w-md text-xs text-muted-foreground">{check.message}</p>
      </TableCell>
    </TableRow>
  );
}

export function DataHealthPanel() {
  const { data: report, isLoading } = useDataHealthReport();

  if (isLoading || !report) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  const violatedRules = report.rules.filter((r) => r.count > 0);
  const cleanRules = report.rules.filter((r) => r.count === 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HeartPulse className="size-4" /> Data health
          </CardTitle>
          <CardDescription>
            Two independent checks: a per-account balance check (does the ledger reconcile to the
            declared statement balance?) and a classification lint (do transfer classifications hold
            their invariants?). Findings are computed live — nothing is stored, and fixes happen in
            the surfaces named below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {report.stats.errors} error(s) · {report.stats.warnings} warning(s) ·{" "}
            {report.stats.accountsChecked} account(s) scanned (
            {report.stats.accountsWithDeclaredBalance} with a declared statement balance) ·{" "}
            {report.stats.transactionsScanned} transaction(s) · {report.stats.pendingReviewEntries}{" "}
            pending transfer review(s)
          </p>
          <p className="text-xs text-muted-foreground">
            Generated {formatDate(report.generatedAt)} · run a scheduled check from Jobs →
            data-health-check
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Balance check</CardTitle>
          <CardDescription>
            currentBalance vs lastStatementBalance + net flows since the statement date (Q15).
            Deltas are exact; declared balances older than 45 days are flagged as stale and
            non-blocking.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Ledger math</TableHead>
                  <TableHead>Verdict</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.balanceChecks.map((check) => (
                  <BalanceRow key={check.accountId} check={check} />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Classification lint</CardTitle>
          <CardDescription>
            Invariants over transfers, card repayments, loan expenses and categorization — the
            checks reconciliation alone cannot make (plan §7's verified negative result).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {violatedRules.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="No violations"
              description="Every classification invariant holds. The balance check above still reports reconciliation separately."
            />
          ) : (
            violatedRules.map((rule) => (
              <div key={rule.ruleId} className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {severityBadge(rule.severity)}
                  <p className="text-sm font-medium text-foreground">
                    {rule.title} · {rule.count}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">{rule.description}</p>
                <p className="text-xs text-foreground">Fix: {rule.suggestedFix}</p>
                <ul className="space-y-1">
                  {report.violations
                    .filter((v) => v.ruleId === rule.ruleId)
                    .slice(0, 10)
                    .map((v, i) => (
                      <li
                        key={`${v.ruleId}-${v.transactionIds.join("-")}-${i}`}
                        className="rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-foreground"
                      >
                        {v.message}
                      </li>
                    ))}
                </ul>
                {report.violations.filter((v) => v.ruleId === rule.ruleId).length > 10 && (
                  <p className="text-xs text-muted-foreground">
                    …and {report.violations.filter((v) => v.ruleId === rule.ruleId).length - 10}{" "}
                    more — run the data-health-check job for the full log.
                  </p>
                )}
              </div>
            ))
          )}
          {cleanRules.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Clean: {cleanRules.map((r) => r.title.toLowerCase()).join(", ")}.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
